import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { completeMicrosoftWebSso } from '../../shared/api/auth';
import { useAuthStore } from '../../shared/hooks/useAuthStore';
import { ApiError } from '../../shared/api/client';

/**
 * Landing page for the browser-based Microsoft SSO flow.
 *
 * The server redirects the user here with `?exchange=…&returnTo=…` after a
 * successful auth + provisioning round-trip. This component immediately
 * POSTs the one-time exchange code to /auth/microsoft/web/exchange to
 * retrieve the access + refresh tokens, then navigates to `returnTo` (or `/`).
 *
 * Strict-mode double-render guard: the exchange code is single-use, so we
 * gate the effect with a ref so the same code isn't redeemed twice.
 */
export function SsoCompletePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current) return;
    triedRef.current = true;

    const exchange = params.get('exchange');
    const returnTo = params.get('returnTo') ?? '/';

    if (!exchange) {
      setError('SSO link is missing the exchange code. Please sign in again.');
      return;
    }

    void (async () => {
      try {
        const res = await completeMicrosoftWebSso(exchange);
        // Wipe any persisted React Query cache from a previous session.
        queryClient.clear();
        setUser(res.user);
        // Strip the query so a refresh of the landed page doesn't try to
        // redeem the (now invalidated) one-time code again.
        navigate(returnTo, { replace: true });
      } catch (err) {
        if (err instanceof ApiError) setError(err.body.message);
        else if (err instanceof Error) setError(err.message);
        else setError('Microsoft sign-in could not be completed.');
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div
      className="relative min-h-full w-full flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, #0f172a 0%, #11233f 55%, #0e2a3a 100%)',
      }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-soft-lg text-center">
        {error ? (
          <>
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-3 text-sm text-rose-300 mb-4">
              {error}
            </div>
            <Link to="/login" className="btn-primary inline-flex">
              Back to sign in
            </Link>
          </>
        ) : (
          <div className="flex items-center justify-center gap-3 text-slate-300">
            <span
              className="inline-block h-4 w-4 rounded-full border-2 border-slate-500 border-t-brand-400 animate-spin"
              aria-hidden="true"
            />
            <span className="text-sm">Finishing Microsoft sign-in…</span>
          </div>
        )}
      </div>
    </div>
  );
}
